import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { Combobox } from '@/components/ui/combobox';
import { useCreateUser } from '@/hooks/use-users';
import { useMerchants } from '@/hooks/use-merchants';
import { ApiError } from '@/lib/api/client';
import type { CreateUserRequest } from '@/types/user';

const ROLE_OPTIONS = [
  { value: 'platform_admin', label: 'Platform Admin' },
  { value: 'merchant_admin', label: 'Merchant Admin' },
  { value: 'merchant_viewer', label: 'Merchant Viewer' },
] as const;

/** Rollen, die einen Merchant-Bezug erfordern */
const MERCHANT_ROLES = new Set(['merchant_admin', 'merchant_viewer']);

export function UserCreatePage() {
  const navigate = useNavigate();
  const createMutation = useCreateUser();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [merchantId, setMerchantId] = useState('');

  // Merchants laden fuer Combobox (nur relevant bei Merchant-Rollen)
  const isMerchantRole = MERCHANT_ROLES.has(role);
  const { data: merchantData, isLoading: merchantsLoading } = useMerchants({ limit: 200 });

  const merchantOptions = (merchantData?.data ?? []).map((m) => ({
    value: m.merchantId,
    label: m.name,
    description: m.merchantId,
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload: CreateUserRequest = {
      email: email.trim(),
      role,
    };
    if (isMerchantRole && merchantId) {
      payload.merchantId = merchantId;
    }

    try {
      await createMutation.mutateAsync(payload);
      toast.success('Benutzer angelegt — Einladung wurde versendet.');
      navigate('/users');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          toast.error('Ein Benutzer mit dieser E-Mail existiert bereits.');
        } else if (err.status === 429) {
          toast.error('Zu viele Anfragen. Bitte warten.');
        } else if (err.status === 400) {
          const detail = err.details?.map((d) => `${d.field}: ${d.message}`).join(', ');
          toast.error(detail ?? err.message);
        } else {
          toast.error(err.message);
        }
      } else {
        toast.error(String(err));
      }
    }
  };

  const isValid = email.trim().length > 0 && role.length > 0 && (!isMerchantRole || merchantId.length > 0);

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Neuer Benutzer</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Neuen Admin-Benutzer einladen — erhaelt eine E-Mail mit temporaerem Passwort.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/users')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zurueck
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Benutzer anlegen</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6 max-w-lg">
            {/* E-Mail */}
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail-Adresse</Label>
              <Input
                id="email"
                type="email"
                required
                placeholder="benutzer@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="off"
              />
            </div>

            {/* Rolle */}
            <div className="space-y-2">
              <Label htmlFor="role">Rolle</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger id="role">
                  <SelectValue placeholder="Rolle waehlen…" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Merchant-ID — nur bei Merchant-Rollen sichtbar */}
            {isMerchantRole && (
              <div className="space-y-2">
                <Label htmlFor="merchantId">Merchant</Label>
                <Combobox
                  id="merchantId"
                  options={merchantOptions}
                  value={merchantId}
                  onChange={setMerchantId}
                  placeholder="Merchant waehlen…"
                  searchPlaceholder="Merchant suchen…"
                  emptyText="Keine Merchants gefunden."
                  loading={merchantsLoading}
                />
                <p className="text-xs text-muted-foreground">
                  Der Benutzer wird diesem Merchant zugeordnet.
                </p>
              </div>
            )}

            {/* Hinweis */}
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
              Der Benutzer erhaelt eine Einladungs-E-Mail mit temporaerem Passwort.
              Beim ersten Login muss ein eigenes Passwort gesetzt werden.
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                type="submit"
                loading={createMutation.isPending}
                disabled={!isValid || createMutation.isPending}
              >
                Einladung senden
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/users')}
              >
                Abbrechen
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
